import { t } from "@redotech/redo-i18n";
import HeartHandIcon from "@redotech/redo-icons/heart-hand_filled.svg";
import MarkerPinIcon from "@redotech/redo-icons/marker-pin-01.svg";
import { Step } from "@redotech/redo-return-core-common/return-flow";
import {
  donateFromModel,
  setReturnLocationsFromModel,
} from "@redotech/redo-return-core-common/return-flow-step-from-model";
import {
  type DonateState,
  isDonateStepValid,
  isSetReturnLocationsStepValid,
  type SetReturnLocationsState,
  SPECIAL_LOCATION_ID,
} from "@redotech/redo-return-core-common/return-flow-step-valid";
import { STEP_TYPE_NAMES } from "@redotech/redo-return-core-common/return-flow/step-type-names";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import React from "react";
import { StepType, StepTypeDetailsProps } from "../step";

export const SET_RETURN_LOCATIONS = (
  Details: React.ComponentType<StepTypeDetailsProps<SetReturnLocationsState>>,
): StepType<SetReturnLocationsState, Step.SetReturnLocations> => ({
  layout() {
    return BlockLayout.FULL;
  },
  empty: { locations: [] },

  title: STEP_TYPE_NAMES[Step.SET_RETURN_LOCATIONS],

  customTitle: (state) => state.customTitle,

  description: (state) => {
    const validCount = state.locations.filter((loc) => loc !== "").length;
    return t("{{count}} locations", { count: validCount });
  },

  Icon: MarkerPinIcon,
  iconColor: "success",

  Details(props) {
    return <Details {...props} />;
  },

  downstream: () => [],

  maxDownstream: () => 0,

  fromModel: (model) => setReturnLocationsFromModel(model),

  toModel: (state) => {
    const regularLocations: string[] = [];
    let useOriginalLocation = false;
    let useOriginalFulfillmentAddress = false;
    let useRedoManaged = false;

    for (const locationId of state.locations) {
      if (!locationId) {
        continue;
      }

      switch (locationId) {
        case SPECIAL_LOCATION_ID.ORIGINAL: {
          useOriginalLocation = true;

          break;
        }
        case SPECIAL_LOCATION_ID.SHIPMENT_ORIGIN: {
          useOriginalFulfillmentAddress = true;

          break;
        }
        case SPECIAL_LOCATION_ID.REDO_MANAGED: {
          useRedoManaged = true;

          break;
        }
        default: {
          regularLocations.push(locationId);
        }
      }
    }

    return {
      type: Step.SET_RETURN_LOCATIONS,
      locations: regularLocations,
      customTitle: state.customTitle,
      restockExternalLocationIds: state.restockExternalLocationIds,
      useOriginalLocation: useOriginalLocation || undefined,
      useOriginalFulfillmentAddress: useOriginalFulfillmentAddress || undefined,
      useRedoManaged: useRedoManaged || undefined,
      rateSourceSelection: state.rateSourceSelection,
    };
  },

  valid: (state) => isSetReturnLocationsStepValid(state).valid,

  stepDeleted: (state) => state,
});

export const DONATE = (
  Details: React.ComponentType<StepTypeDetailsProps<DonateState>>,
): StepType<DonateState, Step.SetReturnLocations> => ({
  layout() {
    return BlockLayout.FULL;
  },
  empty: { locations: [] },

  get title() {
    return t("Donate");
  },

  customTitle: (state) => state.customTitle,

  description: (state) => {
    const validCount = state.locations.filter((loc) => loc !== "").length;
    return validCount > 0
      ? t("{{count}} fallback locations", { count: validCount })
      : t("No fallback location");
  },

  Icon: HeartHandIcon,
  iconColor: "success",

  Details(props) {
    return <Details {...props} />;
  },

  downstream: () => [],

  maxDownstream: () => 0,

  fromModel: (model) => donateFromModel(model),

  toModel: (state) => {
    const regularLocations: string[] = [];
    let useOriginalLocation = false;
    let useOriginalFulfillmentAddress = false;
    let useRedoManaged = false;

    for (const locationId of state.locations) {
      if (!locationId) {
        continue;
      }

      switch (locationId) {
        case SPECIAL_LOCATION_ID.ORIGINAL: {
          useOriginalLocation = true;

          break;
        }
        case SPECIAL_LOCATION_ID.SHIPMENT_ORIGIN: {
          useOriginalFulfillmentAddress = true;

          break;
        }
        case SPECIAL_LOCATION_ID.REDO_MANAGED: {
          useRedoManaged = true;

          break;
        }
        default: {
          regularLocations.push(locationId);
        }
      }
    }

    return {
      type: Step.SET_RETURN_LOCATIONS,
      locations: regularLocations,
      restockExternalLocationIds: state.restockExternalLocationIds,
      customTitle: state.customTitle,
      useOriginalLocation: useOriginalLocation || undefined,
      useOriginalFulfillmentAddress: useOriginalFulfillmentAddress || undefined,
      useRedoManaged: useRedoManaged || undefined,
      useDonation: true,
    };
  },

  valid: (state) => isDonateStepValid(state).valid,

  stepDeleted: (state) => state,
});

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export {
  SPECIAL_LOCATION_ID,
  type SpecialLocationId,
} from "@redotech/redo-return-core-common/return-flow-step-valid";
